import { Grid, GridSize, MenuItem } from "@material-ui/core";
import { Field, FormikErrors, FormikTouched } from "formik";
import * as React from "react";
import { discoveryOptions, getJobCategoryOptions } from "../../constants";
import { t } from "../../i18n/util";
import { applicationStore } from "../../stores/ApplicationStore";
import { IGetApplicantDetailsResponse, PostApplicantPayload } from "../../types/models";
import { CustomInputField } from "../ui/CustomInputField";
import { ThreeFieldGridItem } from "../ui/Primitives";
import { THEME } from "../../theme/ThemeStore";
import { optionToString } from "../util/helpers";
import { useJobCategories } from "../hooks/useJobCategories";

type Props = {
    errors: FormikErrors<PostApplicantPayload | IGetApplicantDetailsResponse> & { jobCategoryId?: string };
    touched: FormikTouched<PostApplicantPayload | IGetApplicantDetailsResponse> & { jobCategoryId?: boolean };
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    lastFieldMd?: GridSize;
    lastFieldLg?: GridSize;
    children?: React.ReactNode;
    showCustomDiscoverySourceField?: boolean;
    customDiscoverySourceMd?: GridSize;
    customDiscoverySourceLg?: GridSize;
};

export const PersonalDataGrid = ({
    errors,
    touched,
    setFieldValue,
    lastFieldMd,
    lastFieldLg,
    children,
    showCustomDiscoverySourceField,
    customDiscoverySourceMd,
    customDiscoverySourceLg
}: Props) => {

    const { jobCategories } = useJobCategories(
        THEME.THEME_NAME === "dal" ? { isDal: true } : { isDal: false }
    );

    const getDynamicJobCategoryOptions = () => {
        return jobCategories
            .filter(category => category.isActive)
            .map(category => ({
                value: category.id,
                label: category.name
            }));
    };

    return (
        <Grid
            container
            spacing={2}
            style={{
                marginBottom: 16
            }}
        >
            <ThreeFieldGridItem>
                <Field
                    component={CustomInputField}
                    label={t("screen.personalData.firstName")}
                    name="firstName"
                    type="text"
                    errorMessage={errors.firstName}
                    isTouched={touched.firstName}
                    required
                />
            </ThreeFieldGridItem>
            <ThreeFieldGridItem>
                <Field
                    component={CustomInputField}
                    label={t("screen.personalData.lastName")}
                    name="lastName"
                    type="text"
                    errorMessage={errors.lastName}
                    isTouched={touched.lastName}
                    required
                />
            </ThreeFieldGridItem>
            <ThreeFieldGridItem>
                <Field
                    component={CustomInputField}
                    label={t("screen.personalData.jobCategory")}
                    name="jobCategoryId"
                    select
                    errorMessage={errors.jobCategoryId as string}
                    isTouched={touched.jobCategoryId as boolean}
                    required
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const selectedCategory = jobCategories.find(cat => cat.id === e.target.value);

                        if (selectedCategory) {
                            setFieldValue('jobCategoryId', selectedCategory.id);
                            setFieldValue('jobCategory', selectedCategory.name);

                            applicationStore.setJobCategory(selectedCategory.name, selectedCategory.id);
                        }
                    }}
                >
                    {
                        getDynamicJobCategoryOptions().map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {optionToString(getJobCategoryOptions(), option.label) || option.label}
                            </MenuItem>
                        ))
                    }
                </Field>
            </ThreeFieldGridItem>
            <ThreeFieldGridItem>
                <Field
                    component={CustomInputField}
                    label={t("screen.personalData.phone")}
                    name="phone"
                    type="tel"
                    errorMessage={errors.phone}
                    isTouched={touched.phone}
                    required
                />
            </ThreeFieldGridItem>
            <ThreeFieldGridItem>
                <Field
                    component={CustomInputField}
                    label={t("screen.personalData.email")}
                    name="email"
                    type="email"
                    errorMessage={errors.email}
                    isTouched={touched.email}
                    required
                />
            </ThreeFieldGridItem>
            <ThreeFieldGridItem>
                <Field
                    component={CustomInputField}
                    label={t("screen.personalData.website")}
                    name="previousProjects"
                    errorMessage={errors.previousProjects}
                    isTouched={touched.previousProjects}
                    optional
                    type="text"
                />
            </ThreeFieldGridItem>
            <Grid item xs={12} sm={12} md={lastFieldMd ?? 12} lg={lastFieldLg ?? 12}>
                <Field
                    component={CustomInputField}
                    label={t("screen.personalData.discovery")}
                    name="discovery"
                    errorMessage={errors.discovery}
                    isTouched={touched.discovery}
                    select
                    required
                >
                    {discoveryOptions.map((option, index) => (
                        <MenuItem key={index} value={option.value}>
                            {t(option.messageId)}
                        </MenuItem>
                    ))}
                </Field>
            </Grid>
            {showCustomDiscoverySourceField && (
                <Grid item xs={12} sm={12} md={customDiscoverySourceMd ?? 12} lg={customDiscoverySourceLg ?? 12}>
                    <Field
                        component={CustomInputField}
                        label={t("screen.personalData.differentDiscoverySource")}
                        name="customDiscoverySource"
                        type="text"
                        errorMessage={errors.customDiscoverySource}
                        isTouched={touched.customDiscoverySource}
                        required
                    />
                </Grid>
            )}
            {children}
            {THEME.THEME_NAME != "dal" && (
                <ThreeFieldGridItem>
                    <Field
                        component={CustomInputField}
                        label={t("screen.personalData.city")}
                        name="city"
                        type="text"
                        errorMessage={errors.city}
                        isTouched={touched.city}
                        required
                    />
                </ThreeFieldGridItem>
            )}
        </Grid>
    );
};